/**
 * Seeks a hidden video to `time` and returns a JPEG data URL of that frame.
 * Does not touch the visible playback video.
 */
export function captureFrameAt(
  video: HTMLVideoElement,
  time: number,
  width = 160,
  height = 90,
): Promise<string | null> {
  return new Promise((resolve) => {
    if (!video || !Number.isFinite(time)) {
      resolve(null)
      return
    }

    const target = Math.max(0, Math.min(time, (video.duration || time) - 0.05))
    let settled = false

    const finish = (url: string | null) => {
      if (settled) return
      settled = true
      video.removeEventListener('seeked', onSeeked)
      video.removeEventListener('error', onError)
      window.clearTimeout(timeout)
      resolve(url)
    }

    const draw = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx || video.readyState < 2) {
          finish(null)
          return
        }
        ctx.drawImage(video, 0, 0, width, height)
        finish(canvas.toDataURL('image/jpeg', 0.72))
      } catch {
        finish(null)
      }
    }

    const onSeeked = () => draw()
    const onError = () => finish(null)
    const timeout = window.setTimeout(() => finish(null), 900)

    video.addEventListener('seeked', onSeeked, { once: true })
    video.addEventListener('error', onError, { once: true })

    // Already close enough — draw immediately
    if (Math.abs(video.currentTime - target) < 0.12 && video.readyState >= 2) {
      draw()
      return
    }

    try {
      video.currentTime = target
    } catch {
      finish(null)
    }
  })
}
